import {ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {LanguageService} from '@spartacus/core';

import {AimoMessage, AimoMessageSeverity, AimoOrderEntry} from '../../../../model/cart.model';
import {AimoNotificationType} from '../../../../model/misc.model';
import {AimoActiveCartService} from '../../../../service/cart/aimo-active-cart.service';
import {AimoGTMProductAttributes} from '../../../../service/gtm/aimo-gtm.model';
import {AimoCurrentProductService} from '../../../../service/product/aimo-current-product.service';
import {AimoProductModalService} from '../../../../service/product/aimo-product-modal.service';
import {AimoMessageService} from '../../../../service/util/aimo-message-service';
import {DateUtils} from '../../../../shared/util/date-utils';

const WARNING_MESSAGE_ID = 'cart-warning-';

@Component({
    selector: 'aimo-cart-item-errors',
    templateUrl: './aimo-cart-item-errors.component.html',
    standalone: false
})
export class AimoCartItemErrorsComponent implements OnInit {
    entry: AimoOrderEntry;
    @Input() messageClass: string;
    @Input() showCloseIcon: boolean = false;
    @Input() gtmProductAttributes: AimoGTMProductAttributes;
    @Input() cartCode: string;

    messages?: MessageWrapper[];

    constructor(
        protected activeCartService: AimoActiveCartService,
        protected currentProductService: AimoCurrentProductService,
        protected languageService: LanguageService,
        protected cdr: ChangeDetectorRef,
        protected messageService: AimoMessageService,
        protected productModalService: AimoProductModalService,
    ) {
    }

    ngOnInit(): void {
        this.init();
    }

    @Input() set aimoEntry(e: AimoOrderEntry) {
        this.entry = e;
        this.init();
    }

    private init() {
        this.messages = this.entry?.messages ? this.entry?.messages.map((m) => this.createWrapper(m)) : [];
        if (this.entry?.closingTimeWarningDate) {
            const key = 'aimo.cart.closingTimeWarning';
            const intervalId = setInterval(() => {
                if (this.entry.closingTimeWarningDate?.getTime() <= new Date().getTime() && this.messages?.find((mes) => mes.message.messageKey === key) === undefined) {
                    this.messages.push(
                        this.createWrapper({
                            messageKey: key,
                            severity: AimoMessageSeverity.WARNING,
                        } as AimoMessage),
                    );
                    this.cdr.detectChanges();
                    clearInterval(intervalId);
                } else {
                    this.messages = this.messages.filter(
                        (mes) => mes.message.messageKey !== key,
                    );
                }
            }, 1000);
        }
    }

    private createWrapper(message: AimoMessage): MessageWrapper {
        return new MessageWrapper(
            message.messageKey ?? message.message,
            message,
            this.split(message.actionParams, 3)
        );
    }

    changeDate(date: string): void {
        this.activeCartService.updateAimoEntry(
            {
                ...this.entry,
                product: {code: this.entry.product.code},
                requestedDeliveryDate: DateUtils.convertDateToOccString(this.languageService, new Date(parseInt(date))),
            },
            this.cartCode,
            this.gtmProductAttributes,
        );
    }

    updateQuantity(quantity: string): void {
        this.activeCartService.updateAimoEntry(
            {
                entryNumber: this.entry.entryNumber,
                product: {code: this.entry.product.code},
                quantity: parseFloat(quantity),
            },
            this.cartCode,
            this.gtmProductAttributes,
        );
    }

    isWarningMessage(message: string): boolean {
        return message === AimoNotificationType.WARNING;
    }

    openProductModal(productCode: string, scrollToId?: string): void {
        this.productModalService.openProductModal(productCode, this.gtmProductAttributes, scrollToId);
    }

    alternativeAction(code: string, replace: boolean): void {
        if (replace) {
            this.replaceProduct(code);
        } else {
            this.addProduct(code);
        }
    }

    replaceProduct(code: string): void {
        this.activeCartService.updateAimoEntry(
            {...this.entry, quantity: 1, product: {...this.entry.product, code: code}},
            this.cartCode,
            this.gtmProductAttributes,
        );
    }

    addProduct(code: string): void {
        this.activeCartService.addAimoEntry(
            code,
            1,
            this.gtmProductAttributes,
            this.cartCode,
        );
    }

    private split(actionParams: string[], groupSize: number): SplitParams[] {
        const ret: SplitParams[] = [];
        for (let i = 0; i < actionParams?.length; ++i) {
            const splitParam: string[] = [];
            for (let j = 0; j < groupSize; ++j) {
                splitParam.push(actionParams[i + j]);
            }
            i += groupSize - 1;
            ret.push({params: splitParam} as SplitParams);
        }
        return ret;
    }

    hideMessage(): void {
        this.messageService.hideMessage(this.getMessageId());
    }

    isMessageHidden(): boolean {
        return this.messageService.isMessageHidden(this.getMessageId());
    }

    private getMessageId(): string {
        return WARNING_MESSAGE_ID + '_' + this.entry.pk + '_' + this.entry.quantity;
    }
}

class SplitParams {
    constructor(public params: string[]) {
    }
}

const MESSAGE_GROUP_SIZE = 2;

class MessageWrapper {
    showAll = false;

    constructor(public id: string, public message: AimoMessage, public split: SplitParams[]) {
    }

    toggle(): void {
        this.showAll = !this.showAll;
    }

    getParams(): SplitParams[] {
        return this.showAll ? this.split : this.split.slice(0, MESSAGE_GROUP_SIZE);
    }

    isShowMore() {
        return this.split.length > MESSAGE_GROUP_SIZE && !this.showAll;
    }

    getRemainingParamSize(): number {
        return this.split.length - MESSAGE_GROUP_SIZE;
    }

}
